import { useLocation, useNavigate } from 'react-router-dom'
import {
  useEmbeddedCatalogParamsOptional,
  useProductsCatalogSearchParams,
} from 'src/pages/Products/embeddedCatalogParams'
import { isCatalogBrowsePath, paths } from 'src/routes/paths'
import { CATALOG_BROWSE_SEARCH_PARAM } from 'src/pages/Products/utils'
import { Column, Row } from 'src/shared/components/Semantic/all'
import { Button } from 'src/shared/components/ui/button'
import { Category, SubCategory } from '../types'
import { useLocalization } from 'src/shared/hooks/useLocalization'

const ExpandedSubcategoryButton = ({
  sub,
  onClick,
}: {
  sub: SubCategory
  onClick: () => void
}) => {
  const { t } = useLocalization()

  return (
    <button
      type="button"
      onClick={onClick}
      className="catalog-category-card rounded-[var(--radius-card-inner)] p-3 sm:p-4 flex items-center justify-center min-h-[72px] min-w-0 cursor-pointer text-left"
    >
      <h4 className="text-center text-sm leading-snug w-full break-words">
        {t(sub.label)}
      </h4>
    </button>
  )
}

export const ExpandedCategoryCard = ({
  category,
  onMinimize,
  onClose,
}: {
  category: Category
  onMinimize: () => void
  onClose?: () => void
}) => {
  const embedded = useEmbeddedCatalogParamsOptional()
  const location = useLocation()
  const navigate = useNavigate()
  const [, setSearchParams] = useProductsCatalogSearchParams()

  const goToCategories = (categoriesValue: string) => {
    if (embedded) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          next.set('categories', categoriesValue)
          next.delete(CATALOG_BROWSE_SEARCH_PARAM)
          return next
        },
        { replace: true }
      )
    } else if (isCatalogBrowsePath(location.pathname)) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          next.set('categories', categoriesValue)
          next.delete(CATALOG_BROWSE_SEARCH_PARAM)
          return next
        },
        { replace: true }
      )
    } else {
      navigate(`${paths.allProducts}?categories=${categoriesValue}`)
    }
  }

  const { t } = useLocalization()

  const handleCategoryClick = (categoryId: string) => {
    goToCategories(categoryId)
    onClose?.()
  }

  const handleSubcategoryClick = (subcategoryId: string) => {
    goToCategories(`${category.id},${subcategoryId}`)
    onClose?.()
  }

  return (
    <div className="absolute inset-0 z-10 overflow-hidden rounded-[var(--radius-card-inner)]">
      <Column className="h-full gap-4 p-1">
        <Row className="items-center gap-2">
          <button
            type="button"
            onClick={() => handleCategoryClick(category.id)}
            className="cursor-pointer transition-opacity hover:opacity-80"
          >
            <h2>{t(category.label)}</h2>
          </button>

          <Button variant="ghost" size="sm" onClick={onMinimize}>
            {t('Back', 'Bakåt')}
          </Button>
        </Row>

        <div className="grid grid-cols-2 gap-3 flex-1 content-start overflow-auto p-1 sm:grid-cols-3">
          {category.subCategories.map((sub) => (
            <ExpandedSubcategoryButton
              key={sub.id}
              sub={sub}
              onClick={() => handleSubcategoryClick(sub.id)}
            />
          ))}
        </div>
      </Column>
    </div>
  )
}
